/**
 * RecordsRequestStatusFilter - Chip-based filter for Records Request statuses.
 *
 * Appears in the toolbar when the Medical Records view is active.
 * Allows users to filter tasks by one or more Records Request statuses.
 * Selection is persisted in URL via the `rrStatus` query parameter.
 */

import { FunnelIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';

import {
  RECORDS_REQUEST_STATUS_LABELS,
  RECORDS_REQUEST_STATUS_ORDER,
} from '../config/groupByConfig';

interface RecordsRequestStatusFilterProps {
  selectedStatuses: string[];
  onChange: (statuses: string[]) => void;
}

const STATUS_OPTIONS = RECORDS_REQUEST_STATUS_ORDER.map((status) => ({
  value: status,
  label: RECORDS_REQUEST_STATUS_LABELS[status] || status,
}));

export const RecordsRequestStatusFilter = ({
  selectedStatuses,
  onChange,
}: RecordsRequestStatusFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const isActive = selectedStatuses.length > 0;

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-md transition-colors ${
          isActive
            ? 'text-zinc-950 dark:text-white bg-zinc-100 dark:bg-zinc-800'
            : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300'
        }`}
      >
        <FunnelIcon className="h-3.5 w-3.5 shrink-0" />
        <span>
          {isActive
            ? `Request Status (${selectedStatuses.length})`
            : 'Request Status'}
        </span>
        {isActive && (
          <XMarkIcon
            className="h-3.5 w-3.5 shrink-0 hover:text-red-500"
            onClick={(e) => {
              e.stopPropagation();
              onChange([]);
            }}
          />
        )}
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setIsOpen(false);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Close records request status filter"
          />

          {/* Dropdown panel */}
          <div className="absolute left-0 mt-1 w-64 bg-white dark:bg-zinc-800 border border-gray-300 dark:border-zinc-700 rounded-md shadow-lg z-20 p-2">
            <div className="mb-2 px-1 text-[10px] font-medium uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
              Filter by Records Request Status
            </div>
            <div className="flex flex-wrap gap-1.5">
              {STATUS_OPTIONS.map((option) => {
                const isSelected = selectedStatuses.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      const newStatuses = isSelected
                        ? selectedStatuses.filter((s) => s !== option.value)
                        : [...selectedStatuses, option.value];
                      onChange(newStatuses);
                    }}
                    className={`px-2 py-1 text-[11px] font-medium rounded-full border transition-colors ${
                      isSelected
                        ? 'bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 border-zinc-900 dark:border-zinc-100'
                        : 'bg-white dark:bg-zinc-900 text-zinc-600 dark:text-zinc-400 border-zinc-950/10 dark:border-white/10 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            {isActive && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="mt-2 w-full text-center text-[10px] text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors"
              >
                Clear all
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};
